import * as React from 'react';
import type { PluginExports } from 'graylog-web-plugin/plugin';

import {
  CreateInvestigationNotificationDetails,
  CreateInvestigationNotificationForm,
  CreateInvestigationNotificationSummary,
} from 'security-app/components/Investigations/notifications';
import { PaginationProvider } from 'common/contexts';
import useLicenseCheck from 'license/useLicenseCheck';
import SecurityBrand from 'security-app/components/SecurityBrand';
import SecurityPage from 'security-app/SecurityPage';
import Routes from 'security-app/routing/Routes';
import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';
import Permissions from 'security-app/Permissions';
import entityRoutesResolver from 'security-app/routes/entityRoutesResolver';
import AssetsField from 'security-app/components/Assets/plugin/AssetsField';
import ExportResultsButton from 'security-app/components/SecurityEvents/Alerts/ExportResultsButton';
import securityEntityPermissionsMapper from 'security-app/SecurityEntityPermissionsMapper';
import AssetEventFormContainer from 'security-app/components/AssetEvents/AssetEventFormContainer';
import AssetEventForm from 'security-app/components/AssetEvents/AssetEventForm';
import AssetEventSummaryContainer from 'security-app/components/AssetEvents/AssetEventSummaryContainer';

import investigationsPlugin from './components/Investigations/plugin';
import assetsPlugin from './components/Assets/plugin';
import sigmaPlugin from './components/SigmaRules/plugin';
import eventProceduresPlugin from './components/SecurityEvents/EventProcedures/plugin';
import securityEventsPlugin from './components/SecurityEvents/Alerts/plugin';
import { InvestigationDrawerProvider } from './components/Investigations/contexts';

import { SECURITY_PAGES } from '../EnterpriseMenuBindings';

export const SECURITY_PERSPECTIVE_ID = 'security';

const createInvestigationNotificationDefaultConfig = {
  create_new_investigation: false,
  investigation_assigned_to: null,
  investigation_id: null,
  investigation_priority: null,
  investigation_status: null,
  type: 'create-investigation-notification-v1',
};

const pluginExports: PluginExports = {
  navigation: SECURITY_PAGES.map((page) => ({ ...page, perspective: SECURITY_PERSPECTIVE_ID })),
  entityPermissionsMapper: securityEntityPermissionsMapper,
  entityRoutes: [entityRoutesResolver],
  pageContextProviders: [InvestigationDrawerProvider, PaginationProvider],
  eventNotificationTypes: [
    {
      type: 'create-investigation-notification-v1',
      displayName: 'Create Investigation Notification [Security]',
      formComponent: CreateInvestigationNotificationForm,
      summaryComponent: CreateInvestigationNotificationSummary,
      detailsComponent: CreateInvestigationNotificationDetails,
      defaultConfig: createInvestigationNotificationDefaultConfig,
    },
  ],
  eventDefinitionTypes: [
    {
      type: 'asset-v1',
      displayName: 'Asset Events',
      sortOrder: 2, // Sort after Filter & Aggregation and Correlation
      description: 'Create Events based on Assets.',
      formComponent: AssetEventFormContainer,
      summaryComponent: AssetEventSummaryContainer,
      defaultConfig: AssetEventForm.defaultConfig,
    },
  ],
  fieldTypeValueRenderer: [
    {
      type: 'associated-assets',
      render: (value: string | Array<string>) => <AssetsField value={value} />,
    },
  ],
  perspectives: [
    {
      id: SECURITY_PERSPECTIVE_ID,
      title: 'Security',
      brandComponent: SecurityBrand,
      welcomeRoute: Routes.WELCOME,
      useCondition: () => {
        const {
          security: { isValid: isValidSecurityLicense },
        } = useLicenseCheck();
        const { permissions } = useCurrentUser();

        return isPermitted(permissions, Permissions.securityApp.read) && isValidSecurityLicense;
      },
    },
  ],
  'events.metrics.actions': [
    { id: 'export-widget', component: (props) => <ExportResultsButton type="alerts" title="alerts" {...props} /> },
  ],
  securityPage: [SecurityPage],
  ...investigationsPlugin,
  ...assetsPlugin,
  ...sigmaPlugin,
  ...eventProceduresPlugin,
  ...securityEventsPlugin,
};

export default pluginExports;
